import React, {useCallback, useContext, useEffect, useMemo, useState} from "react";
import {ConfigProvider, message} from "antd";
import BlockProductsForCompanyModal from "./BlockProductsForCompanyModal";
import {LoadAdminExcelModal} from "./LoadAdminExcelModal";
import {AdminBudgetModal} from "./AdminBudgetModal";
import {AdminCompanyBranding} from "./AdminCompanyBranding";
import {AdminUpdateCompanyDetailsModal} from "./AdminUpdateCompanyDetailsModal";
import columns from "./columns";
import {CompanyPageLayoutModal} from "./CompanyPageLayoutModal";
import {TableDateSearch} from "../components";
import {EditableColumnsTable, PageTitleHeader} from "../../components";
import {HttpClient} from "../../http/HttpClient";
import {useDebounceState, useRequest} from "../../utils/hooks";
import {copyPathToClipboard} from "../../utils/copyToClipboard";
import {AppContext} from "../../AppContext";
import "./admin-companies.css";
import {AddNewCompanyDomainModal} from "./AddNewCompanyDomainModal/AddNewCompanyDomainModal";
import {AdminSubAdminManagerModal} from "../../company/CompanyAccountProfile/AdminSubAdminManagerModal";
import {StringBuilder} from "../../AppUtils.js";
import {TableDateSearchProvider} from "../Context/TableDateSearchContext.jsx";
import moment from "moment";

export const DEFAULT_PAGE = 15;

export const AdminCompanies = () => {
    const {me} = useContext(AppContext);
    const [loadingChangeIgnoreData, setLoadingChangeIgnoreData] = useState(null);
    const [searchDate, setSearchDate] = useState([moment().startOf("day"), moment().endOf("day")]);
    const [searchInput, setSearchInput] = useDebounceState(null, 800);
    const [editCompanyDetails, setEditCompanyDetails] = useState(null);
    const [editCompanyBlockedProducts, setEditCompanyBlockedProducts] = useState(null);
    const [brandCompany, setBrandCompany] = useState(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [companyBudgetModalInfo, setCompanyBudgetModalInfo] = useState(null);
    const [loadSubAdminsExcelModal, setLoadSubAdminsExcelModal] = useState(null);
    const [loadingUpdateCSM, setLoadingUpdateCSM] = useState(null);
    const [companyPageLayoutModal, setCompanyPageLayoutModal] = useState(null);
    const [addNewCompanyDomainModal, setAddNewCompanyDomainModal] = useState(null);
    const [viewSubAdminModal, setViewSubAdminModal] = useState(null);
    const [sort, setSort] = useState(null);
    const [systemIdFilter, setSystemIdFilter] = useState([]);
    const [assignedCSMUserIdsFilter, setAssignedCSMUserIdsFilter] = useState([]);

    const searchText = searchInput?.trim() || undefined;

    const companiesRequestBody = useMemo(() => {
        const [field, order] = sort ? sort.split("-") : [];
        const sortBy = field || "createdAt";
        const sortOrder = order || "asc";
        return {
            startTime: searchDate?.[0]?.valueOf(),
            endTime: searchDate?.[1]?.valueOf(),
            page: currentPage,
            pageSize: DEFAULT_PAGE,
            searchText,
            sortBy,
            sortOrder,
            ...(systemIdFilter?.length ? {systemIds: systemIdFilter} : {}),
            ...(assignedCSMUserIdsFilter?.length ? {assignedCSMUserIds: assignedCSMUserIdsFilter} : {})
        };
    }, [searchDate, currentPage, searchText, sort, systemIdFilter, assignedCSMUserIdsFilter]);

    const [response, isLoading, _, fetchCompanies] = useRequest("/admin/api/companies", "POST", companiesRequestBody, [
        companiesRequestBody
    ]);
    const companies = response?.companies ?? [];
    const totalCount = response?.total ?? 0;

    useEffect(() => {
        setCurrentPage(0);
    }, [searchInput]);

    const onSortCreatedAt = useCallback(() => {
        setSort(prev => {
            if (prev === "createdAt-desc") return null;
            if (prev === "createdAt-asc") return "createdAt-desc";
            return "createdAt-desc";
        });
        setCurrentPage(0);
    }, []);

    const onSystemIdFilterChange = useCallback(selected => {
        setSystemIdFilter(Array.isArray(selected) ? selected : []);
        setCurrentPage(0);
    }, []);

    const onAssignedCSMFilterChange = useCallback(selected => {
        setAssignedCSMUserIdsFilter(Array.isArray(selected) ? selected : []);
        setCurrentPage(0);
    }, []);

    const [systemCSMs] = useRequest("/admin/api/users/csm");

    const companyIds = useMemo(() => {
        return companies?.map(company => company.companyId) ?? [];
    }, [companies]);

    const [masterBudgets, loadingMasterBudgets, __, fetchMasterBudgets] = useRequest(
        "/admin/api/budget/allPositive",
        "POST",
        {companyIds},
        [companyIds],
        companies?.length > 0,
        null,
        {}
    );

    const onIgnoreDataChange = useCallback(
        async (companyId, ignoreData) => {
            setLoadingChangeIgnoreData(companyId);
            try {
                await HttpClient.post(`/admin/api/companies/${companyId}/ignoreData`, {ignoreData});
                companies.find(company => company.companyId === companyId).ignoreData = ignoreData;
            } catch (e) {
                message.error(e.message, 5);
            }
            setLoadingChangeIgnoreData(null);
        },
        [companies]
    );

    const deleteCompany = useCallback(
        async ({companyId}) => {
            const {error} = await HttpClient.safeDelete(`/admin/api/companies/${companyId}`);

            if (error) {
                message.error(error.message, 3);
                return;
            }

            fetchCompanies();
        },
        [fetchCompanies]
    );

    const onSelectCSM = useCallback(
        async (newCSM, companyId) => {
            setLoadingUpdateCSM(companyId);
            const currentCSMs = companies.find(company => company.companyId === companyId)?.assignedCSMs ?? [];

            const [firstCurrentCSM] = currentCSMs;

            if (firstCurrentCSM?.userId === newCSM.userId) {
                setLoadingUpdateCSM(null);
                message.error("מנהלת הלקוח שנבחרה כבר קיימת על החברה הנוכחית");
                return;
            }

            const updatedCompany = await HttpClient.safePost("/admin/api/companies/update", {
                companyId,
                assignedCSMs: [newCSM]
            });

            if (updatedCompany.error) {
                message.error("שגיאה בעידכון CSM");
                setLoadingUpdateCSM(null);
                return;
            }

            innerUpdateCompany(updatedCompany);

            setLoadingUpdateCSM(null);
        },
        [companies]
    );

    const innerUpdateCompany = useCallback(
        updatedCompany => {
            fetchCompanies();
        },
        [fetchCompanies]
    );

    const changeLogo = useCallback(company => {
        setBrandCompany(company);
    }, []);

    const onResendCompanySignupEmail = useCallback(async ({companyId}) => {
        const company = await HttpClient.safePost(`/admin/api/companies/${companyId}/resend`);

        const {error} = company;

        if (error) {
            message.error(error.message, 5);
            return;
        }

        message.success(
            <span>
                Signup email was sent to <strong style={{marginLeft: 3}}>{company.contact}</strong>
            </span>,
            5
        );
    }, []);

    const copyInviteLink = useCallback(({companyId, companyName}) => {
        copyPathToClipboard(`/companies/${companyId}/signup?cn=${btoa(companyName)}`, "קישור הועתק בהצלחה");
    }, []);

    const cancelBudgetRequest = useCallback(
        async ({companyId}) => {
            await HttpClient.delete(`/admin/api/cancelBudgetRequest/${companyId}`);
            fetchCompanies();
        },
        [fetchCompanies]
    );

    const setupPageLayoutForCompany = useCallback(company => {
        setCompanyPageLayoutModal(company);
    }, []);

    const updateCompanyPageLayoutId = useCallback(
        async ({companyId, pageLayoutId, pageLayoutType, selectPageId, pageLayoutRedirectUserTypes}) => {
            const updatedCompany = await HttpClient.safePost("/admin/api/companies/update", {
                companyId,
                pageLayoutId,
                pageLayoutType,
                selectPageId,
                pageLayoutRedirectUserTypes
            });

            if (updatedCompany.error) {
                message.error("שגיאה בעת עידכון דף בית לחברה במערכת");
                return;
            }

            innerUpdateCompany(updatedCompany);
            setCompanyPageLayoutModal(null);

            if (pageLayoutId === null && selectPageId === null) {
                message.success("דף הבית אותחל לעמוד הראשי במערכת");
            } else {
                message.success("דף הבית לחברה הוגדר בהצלחה!");
            }
        },
        []
    );

    const onNewCompanyDomainSave = useCallback(
        async (newCompanyDomain, company) => {
            const updatedCompany = await HttpClient.safePost("/admin/api/companies/update", {
                companyId: company.companyId,
                allowedEmailDomains: [...(company.allowedEmailDomains || []), newCompanyDomain]
            });

            if (updatedCompany.error) {
                message.error("שגיאה, ההערה על החברה לא נשמרה");
                return;
            }

            innerUpdateCompany(updatedCompany);
            setAddNewCompanyDomainModal(null);
            message.success("ההערה על החברה נשמרה בהצלחה");
        },
        [addNewCompanyDomainModal]
    );

    const onViewOrders = useCallback(admin => {
        history.push(`/company/orders/${admin.companyId}?userId=${admin.userId}`);
    }, []);

    const subAdminModalCompany = useMemo(
        () => companies?.find(company => company?.companyId === viewSubAdminModal?.companyId),
        [companies, viewSubAdminModal]
    );

    const onTransferAllCompanyBudgetsToCompanyMaster = useCallback(async company => {
        message.loading({
            content: "הפעולה רצה כעת - אנא המתן...",
            key: "onTransferAllCompanyBudgetsToCompanyMaster"
        });

        const res = await HttpClient.safePost("/admin/api/transferAllCompanyBudgetsToCompanyMaster", {
            companyId: company.companyId
        });

        if (res?.error) {
            message.error({
                content: new StringBuilder("הפעולה נכשלה").append(`(${res?.error})`).toString(),
                key: "onTransferAllCompanyBudgetsToCompanyMaster",
                duration: 5
            });
        } else {
            message.success({
                content: "הפעולה בוצעה בהצלחה",
                key: "onTransferAllCompanyBudgetsToCompanyMaster"
            });
        }
    }, []);

    return (
        <div>
            <ConfigProvider direction="rtl">
                <PageTitleHeader showBack={false}>חברות</PageTitleHeader>
                <AdminCompanyBranding
                    visible={!!brandCompany}
                    companyInfo={brandCompany}
                    onClose={() => {
                        setBrandCompany(null);
                        fetchCompanies();
                    }}
                />
                <CompanyPageLayoutModal
                    visible={!!companyPageLayoutModal}
                    company={companyPageLayoutModal}
                    onClose={() => setCompanyPageLayoutModal(null)}
                    onSave={updateCompanyPageLayoutId}
                />

                <AdminUpdateCompanyDetailsModal
                    visibility={editCompanyDetails}
                    companyDetails={editCompanyDetails}
                    onClose={() => setEditCompanyDetails(null)}
                    onSave={fetchCompanies}
                />
                <BlockProductsForCompanyModal
                    visible={!!editCompanyBlockedProducts}
                    company={editCompanyBlockedProducts}
                    onCancel={() => setEditCompanyBlockedProducts(null)}
                    onSave={() => {
                        fetchCompanies();
                        setEditCompanyBlockedProducts(null);
                    }}
                />
                <AddNewCompanyDomainModal
                    visible={!!addNewCompanyDomainModal}
                    company={addNewCompanyDomainModal}
                    onClose={() => setAddNewCompanyDomainModal(null)}
                    onSave={onNewCompanyDomainSave}
                />
                <AdminBudgetModal
                    onFinish={() => {
                        fetchCompanies();
                        fetchMasterBudgets();
                    }}
                    updateCompany={newCompanyDetails => {
                        innerUpdateCompany(newCompanyDetails);
                        setCompanyBudgetModalInfo(newCompanyDetails);
                    }}
                    onClose={() => setCompanyBudgetModalInfo(null)}
                    companyDetails={companyBudgetModalInfo}
                />
                <LoadAdminExcelModal
                    visible={!!loadSubAdminsExcelModal}
                    onClose={() => setLoadSubAdminsExcelModal(null)}
                    companyId={loadSubAdminsExcelModal?.companyId}
                    onFinish={() => message.success("אדמינים עודכנו בהצלחה")}
                />

                <AdminSubAdminManagerModal
                    visible={!!viewSubAdminModal}
                    company={subAdminModalCompany}
                    onClose={() => setViewSubAdminModal(null)}
                    systemCSMs={systemCSMs}
                    onSelectCompanyMasterCSM={onSelectCSM}
                />

                <div className="admin-companies-top-bar">
                    <div className="admin-companies-top-bar-container">
                        <TableDateSearchProvider>
                            <TableDateSearch
                                date={searchDate}
                                onDatePicked={setSearchDate}
                                onSearch={setSearchInput}
                                todayButtonText="חברות שהצטרפו היום"
                                defaultValue={[moment().subtract(7, "days").startOf("day"), moment().endOf("day")]}
                            />
                        </TableDateSearchProvider>
                    </div>
                </div>
                <EditableColumnsTable
                    name="adminCompaniesTable"
                    editButtonLocation="firstHeader"
                    rowKey="companyId"
                    className="admin-companies-table"
                    scroll={{x: 1240}}
                    pagination={{
                        position: ["bottomRight"],
                        showSizeChanger: false,
                        pageSize: DEFAULT_PAGE,
                        current: currentPage + 1,
                        total: totalCount,
                        showTotal: total => <span>סה״כ {total}</span>,
                        onChange: (page, pageSize) => setCurrentPage(page - 1)
                    }}
                    loading={isLoading}
                    dataSource={companies}
                    columns={columns(
                        currentPage,
                        onIgnoreDataChange,
                        loadingChangeIgnoreData,
                        deleteCompany,
                        company => setEditCompanyDetails(company),
                        setCompanyBudgetModalInfo,
                        onResendCompanySignupEmail,
                        copyInviteLink,
                        changeLogo,
                        setLoadSubAdminsExcelModal,
                        cancelBudgetRequest,
                        masterBudgets,
                        loadingMasterBudgets,
                        systemCSMs,
                        onSelectCSM,
                        loadingUpdateCSM,
                        setEditCompanyBlockedProducts,
                        me,
                        innerUpdateCompany,
                        company => setupPageLayoutForCompany(company),
                        setAddNewCompanyDomainModal,
                        setViewSubAdminModal,
                        onTransferAllCompanyBudgetsToCompanyMaster,
                        onSortCreatedAt,
                        sort,
                        systemIdFilter,
                        assignedCSMUserIdsFilter,
                        onSystemIdFilterChange,
                        onAssignedCSMFilterChange
                    )}
                />
            </ConfigProvider>
        </div>
    );
};
